import {
	LitElement,
	html,
	nothing,
} from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ref } from 'lit/directives/ref.js';
import icons from '../foundation/icons.js';
import style from '../../../public/assets/css/vi-carousel.css';

class ViCarousel extends LitElement {
	static styles = [style];

	static properties = {
		currentIndex: {
			type: Number,
			reflect: true,
		},
		currentElement: {
			type: Object,
			reflect: false,
		},
		bookNav: {
			attribute: 'book-nav',
			type: String,
		},
		maxCaptionBlockSize: {
			type: Number,
		},
		pagination: {
			/* hide or side */
			type: String,
			reflect: true,
		},
	};

	/** @type {null|HTMLSpanElement} */
	#cursorElement;

	#itemsCount = null;

	#isDragging = null;

	#preventClick = false;

	#dragX = null;

	#dragXStart = null;

	#dragForceX = null;

	#dragDeltaX = 0;

	#mouseX = null;

	#mouseY = null;

	translations = {
		en: {
			previous: 'Previous',
			next: 'Next',
			navigationLabel: 'Carousel',
		},
		de: {
			previous: 'Vorheriges',
			next: 'Nächstes',
			navigationLabel: 'Karussell',
		},
	};

	constructor() {
		super();

		this.currentIndex = 1;

		this.pagination = null;

		this.listElement = this.querySelector(':scope > ul');
		this.itemElements = this.listElement.children;
		this.figcaptionElements = this.listElement.querySelectorAll('figcaption');

		this.#itemsCount = this.listElement.childElementCount;
	}

	showItem(index) {
		const itemToShowElement = this.itemElements[index - 1];
		if (itemToShowElement) {
			const scrollLeft = itemToShowElement.offsetLeft
				- itemToShowElement.closest('ul').offsetLeft;
			const behavior = window.matchMedia('(prefers-reduced-motion: no-preference)').matches ? 'smooth' : 'instant';
			this.listElement.scroll({
				left: scrollLeft,
				behavior,
			});
		}
	}

	#setCurrentItem = () => {
		const newIndex = [...this.itemElements].findIndex((_) => _.getAttribute('data-is-intersecting') !== null);

		if (newIndex >= 0) {
			const oldCurrentIndex = this.currentIndex;
			this.currentIndex = newIndex + 1;
			this.currentElement = [...this.itemElements][newIndex];

			if (oldCurrentIndex !== this.currentIndex) {
				const event = new CustomEvent('index-changed', {
					detail: {
						currentIndex: this.currentIndex,
						currentElement: this.currentElement,
					},
				});
				this.dispatchEvent(event);
			}
		}
	};

	#navigtaionItemClick(event) {
		this.showItem(event.target.dataset.index);
	}

	#buttonPreviousClick() {
		if (this.currentIndex - 1 >= 1) {
			this.showItem(this.currentIndex - 1);
		} else {
			this.showItem(this.#itemsCount);
		}
	}

	#buttonNextClick() {
		if (this.currentIndex + 1 <= this.#itemsCount) {
			this.showItem(this.currentIndex + 1);
		} else {
			this.showItem(1);
		}
	}

	#setMaxCaptionBlockSize() {
		let maxHeight = null;
		[...this.figcaptionElements].forEach((_) => {
			const elementHeight = _.clientHeight;
			if (elementHeight > maxHeight) {
				maxHeight = elementHeight;
			}
		});
		this.maxCaptionBlockSize = maxHeight;
	}

	render() {
		const language = this.closest('[lang]')?.lang;
		const translations = this.translations[language] ?? this.translations.en;
		const {
			currentIndex,
			maxCaptionBlockSize,
			pagination,
			bookNav,
		} = this;
		const navigationStyle = maxCaptionBlockSize ? `--max-caption-block-size: ${maxCaptionBlockSize}px` : nothing;

		return html`
			<slot></slot>
			<nav class="navigation" aria-label="${translations.navigationLabel}" style="${navigationStyle}">
				${bookNav === 'cursor' ? html`<span class="cursor" ${ref((_) => { this.#cursorElement = _; })}></span>` : ''}
				<div class="book-navigation">
					<button
						class="${bookNav === 'bottom-left' ? 'a-button' : nothing}"
						data-shape="${bookNav === 'bottom-left' ? 'round' : nothing}"
						data-kind="${bookNav === 'bottom-left' ? 'outline' : nothing}"
						aria-label="${translations.previous}"
						.disabled="${bookNav !== 'cursor' && currentIndex === 1}"
						@click="${this.#buttonPreviousClick}"
					>
						${bookNav !== 'bottom-left' ? html`<span></span>` : ''}
						${bookNav === 'bottom-left' ? unsafeHTML(icons['arrow-left']) : unsafeHTML(icons['arrow-left-s-thin'])}
					</button>
					<button
						class="${bookNav === 'bottom-left' ? 'a-button' : nothing}"
						data-shape="${bookNav === 'bottom-left' ? 'round' : nothing}"
						data-kind="${bookNav === 'bottom-left' ? 'outline' : nothing}"
						aria-label="${translations.next}"
						.disabled="${bookNav !== 'cursor' && currentIndex === this.#itemsCount}"
						@click="${this.#buttonNextClick}"
					>
						${bookNav !== 'bottom-left' ? html`<span></span>` : ''}
						${bookNav === 'bottom-left' ? unsafeHTML(icons['arrow-right']) : unsafeHTML(icons['arrow-right-s-thin'])}
					</button>
				</div>
				${pagination !== 'hide' ? html`
					<ul class="pagination">
						${Array.from({ length: this.#itemsCount }, (_, index) => index + 1).map((index) => html`<li>
							<button
								data-index="${index}"
								aria-label="${index}"
								.ariaPressed="${currentIndex === index ?? nothing}"
								.ariaCurrent="${currentIndex === index ?? nothing}"
								@click="${this.#navigtaionItemClick}"
							>
								<span></span>
							</button>
						</li>`)}
					</ul>
				` : null}
			</nav>
		`;
	}

	connectedCallback() {
		super.connectedCallback();
		let scrollTimer;

		// Functions
		// Utility function to check if an element is partially in view
		const isElementPartiallyInViewport = (element) => {
			const rect = element.getBoundingClientRect();
			const windowHeight = window.innerHeight || document.documentElement.clientHeight;
			const windowWidth = window.innerWidth || document.documentElement.clientWidth;

			return (
				rect.top < windowHeight
				&& rect.bottom > 0
				&& rect.left < windowWidth
				&& rect.right > 0
			);
		};

		// Intersection Observer
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				entry.target.toggleAttribute('data-is-intersecting', entry.isIntersecting);
			});
			this.#setCurrentItem();
		}, {
			threshold: 0.5, // Check if at least 50% of the element is visible
			root: this,
		});
		[...this.itemElements].forEach((_) => {
			observer.observe(_);
		});

		// Event Listener
		const onScrollEnd = () => {
			if (this.#isDragging === false) {
				Object.assign(this.listElement.style, {
					scrollSnapType: null,
					scrollBehavior: null,
				});
			}
		};

		const onPointerDown = (event) => {
			/** @type {HTMLElement} */
			const element = event.target;

			if (element.nodeName !== 'A' && element.closest('a') === null) {
				event.preventDefault();
				this.#isDragging = true;
				this.#dragXStart = event.screenX;
				this.scrollXStart = this.listElement.scrollLeft;
				this.#dragForceX = null;
				this.#dragX = null;
				this.#dragDeltaX = null;
				Object.assign(this.listElement.style, {
					scrollSnapType: 'none',
					scrollBehavior: 'auto',
					cursor: 'grabbing',
				});
			}
		};
		const onPointerMove = (event) => {
			if (this.#isDragging) {
				this.#dragForceX = 1 - this.#dragX / event.screenX;
				this.#dragX = event.screenX;
				this.#dragDeltaX = (this.#dragXStart - event.screenX);
				this.listElement.scroll({
					left: this.scrollXStart + this.#dragDeltaX,
					behavior: 'auto',
				});
			}
		};
		const onPointerUp = () => {
			if (this.#isDragging) {
				let newIndex = this.currentIndex;
				if (Math.abs(this.#dragForceX) > 0.003) {
					if (this.#dragForceX <= 0) {
						newIndex += 1;
					} else {
						newIndex -= 1;
					}
				}
				if (this.#dragDeltaX !== 0) {
					this.showItem(newIndex);
				}
				if (this.#dragDeltaX !== null) {
					this.#preventClick = true;
				}
			}

			this.#isDragging = false;

			Object.assign(this.listElement.style, {
				cursor: null,
			});
		};
		const onClick = (/** @type {MouseEvent} */ event) => {
			if (this.#preventClick === true) {
				event.stopPropagation();
				this.#preventClick = false;
			}
		};
		const onKeyDown = (event) => {
			if (!event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
				if (isElementPartiallyInViewport(this.listElement)) {
					if (event.key === 'ArrowLeft') {
						event.preventDefault();
						this.#buttonPreviousClick();
					} else if (event.key === 'ArrowRight') {
						event.preventDefault();
						this.#buttonNextClick();
					}
				}
			}
		};
		const onResize = () => {
			this.#setMaxCaptionBlockSize();
		};
		const onMouseMoveCursorBookNav = (/** @type {MouseEvent|Event} */ event) => {
			if (event.clientX !== undefined) {
				this.#mouseX = event.clientX;
				this.#mouseY = event.clientY;
			}
			const boundingClientRect = this.getBoundingClientRect();
			const left = boundingClientRect.x * -1 + this.#mouseX;
			const top = boundingClientRect.y * -1 + this.#mouseY;
			const direction = left < boundingClientRect.width / 2 ? 'left' : 'right';
			this.#cursorElement.style.top = `${top}px`;
			this.#cursorElement.style.left = `${left}px`;
			this.#cursorElement.setAttribute('data-direction', direction);
			const elementFromPoint = document.elementFromPoint(this.#mouseX, this.#mouseY);
			if (
				elementFromPoint?.tagName === 'BUTTON'
				|| elementFromPoint?.closest('button')
				|| elementFromPoint?.tagName === 'A'
				|| elementFromPoint?.closest('a')
				|| elementFromPoint?.closest('.m-shopping-dots__popover')
			) {
				this.#cursorElement.toggleAttribute('hidden', true);
			} else {
				this.#cursorElement.toggleAttribute('hidden', false);
			}
		};

		if (this.bookNav === 'cursor' && window.matchMedia('(pointer: fine)').matches) {
			window.addEventListener('scroll', onMouseMoveCursorBookNav);
			this.listElement.addEventListener('mousemove', onMouseMoveCursorBookNav);
			this.listElement.addEventListener('click', (event) => {
				if (
					event.target.tagName !== 'BUTTON'
					&& event.target.closest('button') === null
					&& event.target.closest('.m-shopping-dots__popover') === null
				) {
					if (this.#cursorElement.getAttribute('data-direction') === 'left') {
						this.#buttonPreviousClick();
					} else {
						this.#buttonNextClick();
					}
				}
			});
		} else {
			this.listElement.addEventListener('mousedown', onPointerDown);
			window.addEventListener('mousemove', onPointerMove);
			window.addEventListener('mouseup', onPointerUp);
			window.addEventListener('click', onClick, {
				capture: true,
			});
		}

		document.addEventListener('keydown', onKeyDown);

		this.listElement.addEventListener('scroll', () => {
			clearTimeout(scrollTimer);
			scrollTimer = setTimeout(() => {
				onScrollEnd();
			}, 100);
		}, {
			passive: true,
		});
		window.addEventListener('resize', onResize);

		if (typeof window.requestIdleCallback === 'function') {
			requestIdleCallback(onResize, {
				timeout: 5000,
			});
		} else {
			setTimeout(onResize, 1000);
		}
	}
}

// Define the custom element
if (!customElements.get('vi-carousel')) {
	customElements.define('vi-carousel', ViCarousel);
}

export default ViCarousel;
